import {
    AppRedirectionInfo,
    PAYMENT_CODES,
    PaymentMethodType,
    PaymentMethodUpiSubType,
    PaymentMethodWalletSubType,
} from "@swiggy-private/payment-types";
import { isEmpty } from "@swiggy-private/js-utils";
import { safeJsonParse } from "@swiggy-private/core-ui";
import { buildJuspayMeta } from "./JuspayMetaHelper";
import { getConfirmationTimeInMinutes, getPollingIntervalInMs } from "./AppRedirectionPollingHelper";

type ConvertedAppRedirectionInfo = {
    orderPaymentMethod: string;
    passId: string;
    paymentPartnerOrderId: string;
    paymentTxnId: string;
    primaryPaymentTransactionAmount?: string;

    paymentMethodMeta: {
        paymentRedirectUrl?: string;
        payload?: string;
        iv?: string;
        key?: string;
    };
    phonepeDebitRequisites: {
        webRedirectionUrl?: string;
        base64Body?: string;
        checksum?: string;
        apiEndPoint?: string;
        transactionId?: string;
        s2SCallBackUrl?: string;
    };
    juspayMeta: {
        authTypeForCard?: string; // enabled only for native
        juspayClientAuthToken: string;
        juspayClientAuthTokenExpiry?: number;
    };

    upiIntentUrl?: string;

    // this might be removed , to be confirmed with backend
    upiIntentConfirmTimeInMs: number;
    confirmationTimeMinutes: string;
    pollingIntervalInMs?: number;
    // polling interval time for cred
    credConfirmationTimeMinutes?: string;
    // emi redirect url
    emiRedirectUrl?: string;
};

export function paymentMethodObjToCode(paymentMethodObj?: AppRedirectionInfo["paymentMethodType"]): string | null {
    if (isEmpty(paymentMethodObj)) {
        return null;
    }

    switch (paymentMethodObj.type) {
        case PaymentMethodType.TYPE_CARD: {
            return PAYMENT_CODES.JUSPAY;
        }
        case PaymentMethodType.TYPE_UPI: {
            switch (paymentMethodObj?.upiSubType) {
                case PaymentMethodUpiSubType.UPI_TYPE_COLLECT: {
                    return PAYMENT_CODES.UPICOLLECT;
                }
                case PaymentMethodUpiSubType.UPI_TYPE_INTENT: {
                    return PAYMENT_CODES.UPIINTENT;
                }
            }
            break;
        }
        case PaymentMethodType.TYPE_INAPP_UPI:
            return PAYMENT_CODES.INAPPUPI;
        case PaymentMethodType.TYPE_BNPL: {
            return PAYMENT_CODES.BNPL;
        }
        case PaymentMethodType.TYPE_NB: {
            return PAYMENT_CODES.NETBANKING;
        }
        case PaymentMethodType.TYPE_CASH: {
            return PAYMENT_CODES.CASH;
        }
        case PaymentMethodType.TYPE_SWIGGY_MONEY: {
            return PAYMENT_CODES.SWIGGYPAY;
        }
        case PaymentMethodType.TYPE_WALLET: {
            switch (paymentMethodObj?.walletSubType) {
                case PaymentMethodWalletSubType.WALLET_TYPE_AMAZONPAY: {
                    return PAYMENT_CODES.AMAZONPAY;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_CRED: {
                    return PAYMENT_CODES.CRED;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_FREECHARGE: {
                    return PAYMENT_CODES.FREECHARGE;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_MOBIKWIK: {
                    return PAYMENT_CODES.MOBIKWIK;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_PAYTM: {
                    return PAYMENT_CODES.PAYTM;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_PHONE_PE: {
                    return PAYMENT_CODES.PHONEPE;
                }
            }
            break;
        }

        case PaymentMethodType.TYPE_EMI: {
            return PAYMENT_CODES.THIRD_PARTY_EMI;
        }
    }

    return null;
}

export function paymentMethodTxnId(
    paymentMethodObj?: AppRedirectionInfo["paymentMethodType"],
    appRedirectionInfo?: AppRedirectionInfo,
): string | undefined | null {
    if (isEmpty(paymentMethodObj)) {
        return null;
    }

    switch (paymentMethodObj.type) {
        case PaymentMethodType.TYPE_CARD: {
            return appRedirectionInfo?.cardRedirect?.transactionId;
        }
        case PaymentMethodType.TYPE_UPI: {
            switch (paymentMethodObj?.upiSubType) {
                case PaymentMethodUpiSubType.UPI_TYPE_COLLECT: {
                    return appRedirectionInfo?.upiRedirect?.upiCollectRedirect?.transactionId;
                }
                case PaymentMethodUpiSubType.UPI_TYPE_INTENT: {
                    return appRedirectionInfo?.upiRedirect?.upiIntentRedirect?.transactionId;
                }
            }
            break;
        }
        case PaymentMethodType.TYPE_INAPP_UPI:
            return appRedirectionInfo?.inappUpiRedirect?.transactionId;
        case PaymentMethodType.TYPE_NB: {
            return appRedirectionInfo?.nbRedirect?.transactionId;
        }
        case PaymentMethodType.TYPE_WALLET: {
            switch (paymentMethodObj?.walletSubType) {
                case PaymentMethodWalletSubType.WALLET_TYPE_AMAZONPAY: {
                    return appRedirectionInfo?.walletRedirect?.amazonpayRedirect?.transactionId;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_CRED: {
                    return appRedirectionInfo?.walletRedirect?.credRedirect?.transactionId;
                }
                case PaymentMethodWalletSubType.WALLET_TYPE_PHONE_PE: {
                    return appRedirectionInfo?.walletRedirect?.phonepeRedirect?.sdkRedirect?.transactionId;
                }
            }
            break;
        }
        case PaymentMethodType.TYPE_EMI: {
            return appRedirectionInfo?.emiRedirect?.nativeEmiRedirect?.transactionId;
        }
    }

    return null;
}

export function paymentMethodAuthToken(
    paymentMethodObj?: AppRedirectionInfo["paymentMethodType"],
    appRedirectionInfo?: AppRedirectionInfo,
): string | undefined | null {
    if (isEmpty(paymentMethodObj)) {
        return null;
    }

    switch (paymentMethodObj.type) {
        case PaymentMethodType.TYPE_CARD: {
            return appRedirectionInfo?.cardRedirect?.authToken;
        }
        case PaymentMethodType.TYPE_NB: {
            return appRedirectionInfo?.nbRedirect?.authToken;
        }
        case PaymentMethodType.TYPE_WALLET: {
            switch (paymentMethodObj?.walletSubType) {
                case PaymentMethodWalletSubType.WALLET_TYPE_CRED: {
                    return appRedirectionInfo?.walletRedirect?.credRedirect?.authToken;
                }
            }
            break;
        }
        case PaymentMethodType.TYPE_EMI: {
            return appRedirectionInfo?.emiRedirect?.nativeEmiRedirect?.authToken;
        }
    }

    return null;
}

function getPrimaryPaymentTransactionAmount(
    paymentCode: string,
    appRedirectionInfo: AppRedirectionInfo,
): string | undefined {
    switch (paymentCode) {
        case PAYMENT_CODES.INAPPUPI:
            return appRedirectionInfo?.inappUpiRedirect?.amount;

        // in case of card payment methods return amount from appredirection info obj
        case PAYMENT_CODES.JUSPAY:
            return appRedirectionInfo?.cardRedirect?.amount;

        case PAYMENT_CODES.THIRD_PARTY_EMI: {
            return appRedirectionInfo?.emiRedirect?.nativeEmiRedirect?.amount;
        }
    }
    return;
}

export function handleAppRedirectionInfoConversion(
    targetedMarketplacePaymentForwardReferenceId: string,
    appRedirectionInfoString?: string,
    legacyOrderPaymentMethod?: string,
): ConvertedAppRedirectionInfo | undefined {
    if (isEmpty(appRedirectionInfoString) || isEmpty(targetedMarketplacePaymentForwardReferenceId)) {
        return undefined;
    }
    const appRedirectionInfo = safeJsonParse<AppRedirectionInfo>(appRedirectionInfoString as string, undefined);

    if (isEmpty(appRedirectionInfo)) {
        return undefined;
    }

    let convertedPaymentMethodCode = paymentMethodObjToCode(appRedirectionInfo?.paymentMethodType);

    if (isEmpty(convertedPaymentMethodCode)) {
        return undefined;
    }

    const phonePeRedirect = appRedirectionInfo?.walletRedirect?.phonepeRedirect;
    const upiRedirect = appRedirectionInfo?.upiRedirect;
    const upiIntentRedirect = upiRedirect?.upiIntentRedirect;
    const upiCollectRedirect = upiRedirect?.upiCollectRedirect;
    const amazonPayRedirect = appRedirectionInfo?.walletRedirect?.amazonpayRedirect;
    const emiPaymentRedirect = appRedirectionInfo?.emiRedirect?.juspayEmiRedirect;

    // fetch redirect url from emipaymentredirect if present
    const emiRedirectUrl = emiPaymentRedirect?.redirectionUrl || "";

    const paymentPartnerOrderId = String(
        paymentMethodTxnId(appRedirectionInfo?.paymentMethodType, appRedirectionInfo) || "",
    );

    // each method would have token inside their own object, so we get from there
    const juspayClientAuthToken = String(
        paymentMethodAuthToken(appRedirectionInfo?.paymentMethodType, appRedirectionInfo) || "",
    );

    const primaryPaymentTransactionAmount = getPrimaryPaymentTransactionAmount(
        convertedPaymentMethodCode,
        appRedirectionInfo,
    );

    const amazonPayPayload = safeJsonParse<{
        paymentRedirectUrl?: string;
        payload?: string;
        key?: string;
        iv?: string;
    }>(amazonPayRedirect?.encryptedPayload || "{}", undefined);

    // Direct paymentUri on amazonpayRedirect (new APay SDK flow, no encryptedPayload)
    const amazonPayDirectPaymentUri = amazonPayRedirect?.paymentUri;

    // As part of SOS migration, payment codes are changing
    // backend no longer has distinction b/w amazon pay sdk and web
    // so to figure that out. client can simply check if paymentUri is present or not
    // Only switch to AMAZONPAYWEB when encryptedPayload contains paymentRedirectUrl (old web flow),
    // NOT when paymentUri is directly on amazonpayRedirect (new SDK flow)
    if (convertedPaymentMethodCode === PAYMENT_CODES.AMAZONPAY && amazonPayPayload?.paymentRedirectUrl) {
        convertedPaymentMethodCode = PAYMENT_CODES.AMAZONPAYWEB;
    }

    const _orderPaymentMethod = legacyOrderPaymentMethod || convertedPaymentMethodCode || "";

    return {
        orderPaymentMethod: _orderPaymentMethod,
        passId: paymentPartnerOrderId,
        paymentPartnerOrderId: paymentPartnerOrderId,
        paymentTxnId: targetedMarketplacePaymentForwardReferenceId,
        primaryPaymentTransactionAmount,

        // Amazon pay
        paymentMethodMeta: {
            paymentRedirectUrl: amazonPayPayload?.paymentRedirectUrl || amazonPayDirectPaymentUri,
            payload: amazonPayPayload?.payload,
            iv: amazonPayPayload?.iv,
            key: amazonPayPayload?.key,
        },

        // Phone pe related fields
        phonepeDebitRequisites: {
            webRedirectionUrl: phonePeRedirect?.webRedirect?.paymentUri,
            base64Body: phonePeRedirect?.sdkRedirect?.base64Body,
            checksum: phonePeRedirect?.sdkRedirect?.checksum,
            apiEndPoint: phonePeRedirect?.sdkRedirect?.apiEndPoint,
            transactionId: phonePeRedirect?.sdkRedirect?.transactionId,
            s2SCallBackUrl: phonePeRedirect?.sdkRedirect?.s2sCallBackUrl,
        },

        juspayMeta: buildJuspayMeta(juspayClientAuthToken, appRedirectionInfo),

        upiIntentUrl: upiIntentRedirect?.upiIntentUrl,

        // this might be removed , to be confirmed with backend
        upiIntentConfirmTimeInMs: 60000, // key not used. adding fallback value as 1min just in case
        // polling duration time for upi intent, upi collect and card - can be fraction values as well
        confirmationTimeMinutes: String(
            getConfirmationTimeInMinutes(
                upiCollectRedirect,
                upiIntentRedirect,
                appRedirectionInfo,
                _orderPaymentMethod,
            ),
        ),
        // polling interval time for upi intent, upi collect and card
        pollingIntervalInMs: getPollingIntervalInMs(appRedirectionInfo),

        // polling duration time for cred
        credConfirmationTimeMinutes: String(
            (appRedirectionInfo?.walletRedirect?.credRedirect?.credConfirmTimeInMs || 60000) / 60000,
        ),

        emiRedirectUrl,
    };
}
